import { useEffect, useState, memo, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/legacy/image'
import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
// Components
import Button from '../newButton/Button'
import styles from './Explore.module.scss'
// Utils
import { getBreedImage, pickBreeds } from './lib/utils'
import { ExploreDogsType, IExploreBreeds } from '@types'

interface IBreedCard {
  breed: ExploreDogsType
  isLanding?: boolean
}

const BreedCard = memo(({ breed, isLanding }: IBreedCard) => (
  <div
    className={cn(
      styles.breed,
      breed?.hasDogs ? styles.withDogs : styles.noDogs
    )}
  >
    <Link
      href={
        isLanding
          ? `/puppies-for-sale/${breed?.slug}`
          : `/search?breed=${breed?.slug}`
      }
      prefetch={false}
    >
      <Image
        src={
          getBreedImage(breed)
            ? `${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${getBreedImage(breed)}`
            : '/placeholder-breed.svg'
        }
        role="img"
        aria-label={`${breed?.name} sitting and posing`}
        alt={`${breed?.name} sitting and posing`}
        width={165}
        height={165}
        loading="lazy"
        layout="responsive"
      />
      <p>{breed?.name}</p>
    </Link>
  </div>
))

interface IExplore {
  breeds: IExploreBreeds
  title: string
  hasSearch?: boolean
  isLanding?: boolean
}

export default function Explore({
  breeds: _breeds,
  title,
  hasSearch,
  isLanding,
}: IExplore) {
  const [breedsShowNumber, setBreedsShowNumber] = useState(0)

  const [hasLoaded, setHasLoaded] = useState(false)

  const smallBreedData = [
    ...(_breeds?.withDogs || []),
    ...(_breeds?.withoutDogs || []),
  ].sort((a, b) => (a.count > b.count ? -1 : 1))

  let showMoreButton = true
  let showLessButton = false
  // Search
  const [inputText, setInputText] = useState('')
  const inputHandler = (e) => {
    // convert input text to lower case
    const lowerCase = e.target.value.toLowerCase()
    setInputText(lowerCase)
  }

  // Check if Javascript has fully loaded
  useEffect(() => {
    setHasLoaded(true)
  })

  useEffect(() => {
    if (isLanding) {
      setBreedsShowNumber(16)
    } else {
      setBreedsShowNumber(12)
    }
  }, [])

  // Set array with Search
  const filteredBreeds = smallBreedData?.filter((breed) =>
    breed.slug.toLowerCase().includes(inputText)
  )

  // desktop
  const breeds = useMemo(
    () => filteredBreeds.slice(0, breedsShowNumber),
    [filteredBreeds, breedsShowNumber]
  )
  // Buttons showing conditions
  if (
    (isLanding && breedsShowNumber > 16) ||
    (!isLanding && breedsShowNumber > 12)
  ) {
    showLessButton = true
  }

  if (breeds.length === filteredBreeds.length) showMoreButton = false

  // mobile
  const pairBreeds = useMemo(
    () => (hasSearch ? pickBreeds(filteredBreeds) : pickBreeds(smallBreedData)),
    [hasSearch, filteredBreeds, smallBreedData]
  )

  return (
    <div className={styles.explore} id="explore">
      {hasSearch ? (
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}> {title} </h2>
          <input
            type="search"
            placeholder="Search breed"
            onChange={inputHandler}
          />
        </div>
      ) : (
        <div className={styles.header}>
          <h2>{title}</h2>
          <a
            href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale#breed`}
          >
            View All
          </a>
        </div>
      )}

      {hasSearch && breeds.length === 0 ? (
        <div className={styles.noResult}>
          <p>
            We have no breeds that match this description, please search again!
          </p>
        </div>
      ) : (
        <>
          {/* desktop */}
          <div
            className={cn(
              styles.breeds,
              styles.desktop,
              hasSearch && styles.searchDesktop
            )}
          >
            {breeds?.map((b, i) => (
              <BreedCard key={i} breed={b} isLanding={isLanding} />
            ))}
          </div>
          {/* mobile */}
          <div
            className={cn(
              styles.breeds,
              styles.mobile,
              hasSearch && styles.searchMobile
            )}
          >
            <Swiper
              speed={1000}
              spaceBetween={15}
              initialSlide={0}
              grabCursor
              slidesPerView={3}
            >
              {pairBreeds?.map((pair, i) => (
                <SwiperSlide key={i}>
                  <div className={styles.pair}>
                    <BreedCard breed={pair[0]} isLanding={isLanding} />
                    <BreedCard breed={pair[1]} isLanding={isLanding} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </>
      )}

      {/* No js */}
      {!hasLoaded &&
        smallBreedData.map((b, i) => (
          <a href={`/puppies-for-sale/${b.slug}`} key={i}>
            {b.name}
            <span>{b.count > 1 && `(${b.count} puppies)`}</span>
          </a>
        ))}

      <div className={styles.buttonContainer}>
        {showLessButton && (
          <Button
            title="View less"
            theme={['black', 'medium']}
            rightImage="/search/arrow-top-black.svg"
            width={10}
            height={10}
            onClick={() => {
              if (isLanding) {
                setBreedsShowNumber((curr) => curr - 32)
              } else {
                setBreedsShowNumber((curr) => curr - 24)
              }
            }}
            altImg="Black up arrow"
          />
        )}

        {showMoreButton && (
          <Button
            title="View more"
            theme={['black', 'medium']}
            rightImage="/search/arrow-down-black.svg"
            width={10}
            height={10}
            onClick={() => {
              if (isLanding) {
                setBreedsShowNumber((curr) => curr + 32)
              } else {
                setBreedsShowNumber((curr) => curr + 24)
              }
            }}
            altImg="Black down arrow"
          />
        )}
      </div>
    </div>
  )
}
